import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { copy, REMINDER_NOTE_PRESETS, WORKING_HOURS_END, WORKING_HOURS_START } from "@wheels-crm/shared-types";
import type { Database } from "@wheels-crm/shared-types";
import { supabase } from "../lib/supabaseClient";
import { normalizePhone } from "../lib/phone";
import { toReminderDueAt } from "../lib/workingHours";
import { OrderForm, type OrderFormValues } from "../components/orders/OrderForm";
import { Button } from "../components/ui/Button";
import { FieldWrap, Input } from "../components/ui/Input";
import { ReminderBadge } from "../components/orders/ReminderBadge";

type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ClientRow = Database["public"]["Tables"]["clients"]["Row"];
type ReminderRow = Database["public"]["Tables"]["reminders"]["Row"];

function toFormValues(order: OrderRow, client: ClientRow): OrderFormValues {
  return {
    clientName: client.name,
    phone: client.phone,
    sizeSpec: (order.size_spec ?? "") as OrderFormValues["sizeSpec"],
    carBrand: order.car_brand ?? "",
    carYear: order.car_year ? String(order.car_year) : "",
    source: order.source,
    purchasePrice: order.purchase_price != null ? String(order.purchase_price) : "",
    amount: order.amount != null ? String(order.amount) : "",
    prepayment: order.prepayment != null ? String(order.prepayment) : "",
    extraSales: order.extra_sales != null ? String(order.extra_sales) : "",
    deliveryCost: order.delivery_cost != null ? String(order.delivery_cost) : "",
    orderComment: order.order_comment ?? "",
    notes: order.notes ?? "",
  };
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [client, setClient] = useState<ClientRow | null>(null);
  const [reminders, setReminders] = useState<ReminderRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [reminderDate, setReminderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reminderTime, setReminderTime] = useState(WORKING_HOURS_START);
  const [reminderNote, setReminderNote] = useState<string>(copy.reminders.defaultNote);

  async function load() {
    if (!orderId) return;
    const { data, error } = await supabase
      .from("orders")
      .select("*, client:clients(*), reminders(*)")
      .eq("id", orderId)
      .single();
    if (!error && data) {
      const { client: c, reminders: r, ...o } = data as OrderRow & {
        client: ClientRow;
        reminders: ReminderRow[];
      };
      setOrder(o as OrderRow);
      setClient(c);
      setReminders(r);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function handleSave(values: OrderFormValues) {
    if (!order || !client) return;
    const phone = normalizePhone(values.phone);

    await supabase.from("clients").update({ name: values.clientName, phone }).eq("id", client.id);

    await supabase
      .from("orders")
      .update({
        size_spec: order.product_line === "wheels" ? values.sizeSpec : null,
        car_brand: values.carBrand || null,
        car_year: values.carYear ? Number(values.carYear) : null,
        source: values.source,
        purchase_price: values.purchasePrice ? Number(values.purchasePrice) : null,
        amount: values.amount ? Number(values.amount) : null,
        prepayment: values.prepayment ? Number(values.prepayment) : null,
        extra_sales: values.extraSales ? Number(values.extraSales) : null,
        delivery_cost: values.deliveryCost ? Number(values.deliveryCost) : null,
        order_comment: values.orderComment || null,
        notes: values.notes || null,
      })
      .eq("id", order.id);

    const {
      data: { user },
    } = await supabase.auth.getUser();
    await supabase.from("order_events").insert({
      order_id: order.id,
      actor: user?.id,
      event_type: "field_updated",
      payload: {},
    });

    await load();
  }

  async function addReminder(e: React.FormEvent) {
    e.preventDefault();
    if (!order) return;
    const {
      data: { user },
    } = await supabase.auth.getUser();
    await supabase.from("reminders").insert({
      order_id: order.id,
      due_at: toReminderDueAt(reminderDate, reminderTime),
      note: reminderNote || copy.reminders.defaultNote,
      created_by: user?.id,
    });
    setReminderNote(copy.reminders.defaultNote);
    await load();
  }

  async function markReminderDone(id: string) {
    await supabase.from("reminders").update({ is_done: true }).eq("id", id);
    await load();
  }

  if (loading) return <p>Завантаження…</p>;
  if (!order || !client) return <p className="empty-state">{copy.emptyState}</p>;

  return (
    <div>
      <div className="page-header">
        <h1>
          {client.name} — {order.size_spec ?? copy.productLine[order.product_line]}
        </h1>
        <Button onClick={() => navigate("/orders")}>{copy.back}</Button>
      </div>

      <div className="widget-grid">
        <OrderForm
          initial={toFormValues(order, client)}
          submitLabel={copy.save}
          onSubmit={handleSave}
          onCancel={() => navigate("/orders")}
          productLine={order.product_line}
        />

        <div className="card">
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.nav.reminders}</h2>

          {reminders.length === 0 && <p className="empty-state">{copy.emptyState}</p>}
          {reminders.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 0",
                opacity: r.is_done ? 0.5 : 1,
              }}
            >
              <div>
                <ReminderBadge reminder={r.is_done ? null : r} />
                {r.is_done && <span style={{ fontSize: 13 }}>{r.note}</span>}
                {!r.is_done && <span style={{ fontSize: 13 }}> — {r.note}</span>}
              </div>
              {!r.is_done && (
                <Button onClick={() => markReminderDone(r.id)}>{copy.reminders.markDone}</Button>
              )}
            </div>
          ))}

          <form onSubmit={addReminder} style={{ marginTop: 16, borderTop: "1px solid var(--color-border)", paddingTop: 12 }}>
            <FieldWrap label={copy.reminders.date} htmlFor="reminderDate">
              <Input
                id="reminderDate"
                type="date"
                required
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
              />
            </FieldWrap>
            <FieldWrap label={copy.reminders.time} htmlFor="reminderTime">
              <Input
                id="reminderTime"
                type="time"
                min={WORKING_HOURS_START}
                max={WORKING_HOURS_END}
                step={900}
                required
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
              />
            </FieldWrap>
            <FieldWrap label={copy.reminders.note} htmlFor="reminderNote">
              <Input
                id="reminderNote"
                list="reminder-note-presets"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
              />
              <datalist id="reminder-note-presets">
                {REMINDER_NOTE_PRESETS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </FieldWrap>
            <Button type="submit" variant="primary">
              {copy.save}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
